/**
 * Размытая копия картинки — для фона за тайтлом.
 *
 * Раньше «размытием» была маленькая JPEG-копия (92–185 точек), растянутая
 * браузером на весь экран. Дёшево, но растягивалось всё, что в ней есть:
 * квадраты сжатия 8×8 становились блоками по 80–180 px с прямыми границами
 * (вертикальная «полоса» у левого края главной — как раз такая граница), а
 * звон вокруг контуров — грязными пятнами.
 *
 * Теперь размытие считается ОДИН раз, на маленькой копии и на обычном холсте:
 *
 *   1. картинка уменьшается до 128 точек по длинной стороне (ступенями не
 *      больше ×2 — так уменьшение усредняет, а не выхватывает точки);
 *   2. по ней четырежды проходит размытие усреднением (≈ гауссово, σ ≈ 2,8
 *      точки, то есть ~2 % ширины кадра) — от блоков и звона не остаётся
 *      ничего, от кадра — крупные формы и цвет. Размытая настолько картинка
 *      растягивается уже без изломов: соседние точки почти равны;
 *   3. при записи в 8 бит к значениям подмешивается шум в пол-ступени:
 *      ровные полосы-ступени на тёмных плавных переходах распадаются.
 *
 * Счёт — девять тысяч точек, доли миллисекунды, и только при смене тайтла;
 * ни `filter: blur`, ни `backdrop-filter` на большом слое нет, каждый кадр
 * браузер по-прежнему просто растягивает маленькую текстуру. Результат
 * кэшируется по адресу.
 */

/**
 * Насколько размыт фон.
 *
 * `deep` — подложка под чётким кадром и под постером: от картинки остаются
 * только пятна цвета; исходник — крошечная копия (w92), и слабее размыть её
 * нельзя — вылезут квадраты сжатия.
 *
 * `light` — фон страницы тайтла и её шагов: в нём должны проступать образы —
 * силуэты, свет, акценты (правка владельца 05.10.2026: «сейчас сильно
 * заблюрено, ничего не разобрать»). Исходник для него крупный (кадр 780
 * точек): при уменьшении в четыре раза квадраты сжатия исчезают сами, и
 * размытию остаётся только смягчить картинку.
 */
export type SoftLook = 'deep' | 'light';

/**
 * Длинная сторона копии, на которой считается размытие, радиус усреднения и
 * число проходов: σ = √(passes·((2r+1)²−1)/12) точек копии. `deep` — σ ≈ 2,8
 * из 128 (2,2 % ширины, но исходник крошечный — от кадра остаются пятна),
 * `light` — σ ≈ 2,4 из 224 (1,1 % ширины; исходник 780 точек): лёгкий
 * расфокус, в котором кадр читается — силуэты, свет, крупные формы. Вдвое
 * слабее прежних 2,2 % (правка владельца 05.10.2026: «сделай везде блюр
 * меньше в два раза»); то же число у приложения. Квадраты сжатия при этом не
 * возвращаются: исходник уменьшается в три с половиной раза.
 */
const LOOKS: Readonly<Record<SoftLook, { grid: number; radius: number; passes: number }>> = {
  deep: { grid: 128, radius: 2, passes: 4 },
  light: { grid: 224, radius: 2, passes: 3 },
};
/** Сколько готовых фонов держим: главная листает десятки тайтлов подряд. */
const CACHE_LIMIT = 24;

const cache = new Map<string, Promise<HTMLCanvasElement | null>>();

/** Готовый размытый фон; `null` — посчитать не вышло (нет холста, ошибка). */
export function softImage(url: string, look: SoftLook = 'deep'): Promise<HTMLCanvasElement | null> {
  const key = `${look}:${url}`;
  const known = cache.get(key);
  if (known) {
    // Свежее — в конец: вытесняется то, к чему давно не возвращались.
    cache.delete(key);
    cache.set(key, known);
    return known;
  }
  const pending = render(url, look).catch(() => null);
  cache.set(key, pending);
  if (cache.size > CACHE_LIMIT) {
    const oldest = cache.keys().next().value;
    if (oldest !== undefined) cache.delete(oldest);
  }
  return pending;
}

function canvasOf(width: number, height: number): [HTMLCanvasElement, CanvasRenderingContext2D] | null {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return null;
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  return [canvas, context];
}

async function render(url: string, look: SoftLook): Promise<HTMLCanvasElement | null> {
  const { grid, radius, passes } = LOOKS[look];
  const image = new Image();
  image.decoding = 'async';
  image.src = url;
  await image.decode();
  const sourceW = image.naturalWidth;
  const sourceH = image.naturalHeight;
  if (sourceW === 0 || sourceH === 0) return null;

  const scale = grid / Math.max(sourceW, sourceH);
  const smallW = Math.max(2, Math.round(sourceW * scale));
  const smallH = Math.max(2, Math.round(sourceH * scale));

  // Уменьшение ступенями: каждая — не больше чем вдвое.
  let current: CanvasImageSource = image;
  let width = sourceW;
  let height = sourceH;
  while (width > smallW * 2 || height > smallH * 2) {
    width = Math.max(smallW, Math.ceil(width / 2));
    height = Math.max(smallH, Math.ceil(height / 2));
    const step = canvasOf(width, height);
    if (step === null) return null;
    step[1].drawImage(current, 0, 0, width, height);
    current = step[0];
  }
  const small = canvasOf(smallW, smallH);
  if (small === null) return null;
  small[1].drawImage(current, 0, 0, smallW, smallH);
  const data = small[1].getImageData(0, 0, smallW, smallH);
  write(data, blurred(planes(data), smallW, smallH, radius, passes));
  small[1].putImageData(data, 0, 0);
  return small[0];
}

/** Три цветовые плоскости числами с запятой: округление — только в конце. */
function planes(data: ImageData): [Float32Array, Float32Array, Float32Array] {
  const count = data.width * data.height;
  const red = new Float32Array(count);
  const green = new Float32Array(count);
  const blue = new Float32Array(count);
  for (let index = 0; index < count; index += 1) {
    red[index] = data.data[index * 4]!;
    green[index] = data.data[index * 4 + 1]!;
    blue[index] = data.data[index * 4 + 2]!;
  }
  return [red, green, blue];
}

/** Размытие усреднением по строкам и столбцам, `passes` раз: три-четыре прохода ≈ гауссово. */
export function blurred(
  source: [Float32Array, Float32Array, Float32Array],
  width: number,
  height: number,
  radius: number,
  passes: number,
): [Float32Array, Float32Array, Float32Array] {
  const scratch = new Float32Array(width * height);
  for (const plane of source) {
    // Строки — во временную плоскость, столбцы — обратно: итог остаётся на месте.
    for (let pass = 0; pass < passes; pass += 1) {
      boxPass(plane, scratch, width, height, radius, true);
      boxPass(scratch, plane, width, height, radius, false);
    }
  }
  return source;
}

/** Один проход усреднения вдоль строк или столбцов; за краем — крайняя точка. */
function boxPass(
  from: Float32Array,
  to: Float32Array,
  width: number,
  height: number,
  radius: number,
  horizontal: boolean,
): void {
  const lines = horizontal ? height : width;
  const length = horizontal ? width : height;
  const stride = horizontal ? 1 : width;
  const span = radius * 2 + 1;
  for (let line = 0; line < lines; line += 1) {
    const start = horizontal ? line * width : line;
    let sum = 0;
    for (let offset = -radius; offset <= radius; offset += 1) {
      sum += from[start + Math.min(length - 1, Math.max(0, offset)) * stride]!;
    }
    for (let position = 0; position < length; position += 1) {
      to[start + position * stride] = sum / span;
      const leaving = Math.max(0, position - radius);
      const entering = Math.min(length - 1, position + radius + 1);
      sum += from[start + entering * stride]! - from[start + leaving * stride]!;
    }
  }
}

/** Запись плоскостей обратно в картинку — с шумом в пол-ступени перед округлением. */
function write(data: ImageData, [red, green, blue]: [Float32Array, Float32Array, Float32Array]): void {
  const count = data.width * data.height;
  for (let index = 0; index < count; index += 1) {
    // Один и тот же сдвиг на три канала: шум остаётся серым, без цветной ряби.
    const noise = Math.random() - Math.random();
    data.data[index * 4] = Math.round(red[index]! + noise);
    data.data[index * 4 + 1] = Math.round(green[index]! + noise);
    data.data[index * 4 + 2] = Math.round(blue[index]! + noise);
    data.data[index * 4 + 3] = 255;
  }
}
